import { Add16, Bullhorn20 } from '@carbon/icons-react';
import Close16 from '@carbon/icons-react/es/close/16';
import { Card, Col, Row, Tooltip, Typography } from 'antd';
import React, { FC, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { AiFillRuntimeInput } from 'api/ai-fill/types';
import { StatusUpdatePrivacy } from 'api/status-updates';
import { TemplateCard } from 'components/atoms/card/template-card';
import {
  CustomLayoutDrawer,
  CustomLayoutDrawerContent,
} from 'components/atoms/drawer';
import { AIFill16 } from 'components/atoms/icons/ai-fill';
import { PageLoading } from 'components/atoms/loading';
import { HelperText01, Text03 } from 'components/styles';
import { Template } from 'data/templates/api';
import { useTranslation } from 'hooks/use-translation';
import { useGetAllStatusUpdateTemplatesQuery } from 'queries/templates/status-update-templates';
import { StatusUpdatesTemplateEmptyState } from '../status-update-empty-state';
import { AIDrawerContent } from './ai-drawer';

const { Paragraph } = Typography;

const CardText = styled(Paragraph)``;

const CreateFromScratchCard = styled(TemplateCard)`
  width: 100%;
`;

const StyledCreateFromScratchIconWrapper = styled.div`
  padding: var(--spacing-03);
  color: var(--link-color);
  background-color: var(--blue-10);
  border-radius: var(--border-radius-default);
  margin-bottom: var(--spacing-02);
  display: flex;
  align-items: center;
`;

const StyledParagraph = styled(Paragraph)`
  color: var(--link-color);
`;

const Help = styled.div`
  ${HelperText01};
  ${Text03};
  margin-top: var(--spacing-02);
  color: var(--gray-70);
`;

const SectionHeader = styled.div`
  font-weight: 590;
`;

const SectionSubText = styled.div`
  ${HelperText01};
`;

const SectionHeaderWrapper = styled.div``;

const SectionContent = styled.div`
  padding-top: var(--spacing-03);
`;

const SectionWrapper = styled.div`
  margin-top: 32px;
`;

const StyledTemplateCard = styled(Card)<{ private?: boolean }>`
  height: 72px;
  padding: var(--spacing-05);
  display: flex;
  align-items: center;
  background: ${(props) =>
    props.private ? 'var(--private-01)' : 'var(--white-100)'};

  .ant-card-body {
    width: 270px;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    padding: 0px;
    .ant-typography {
      margin-bottom: 0;
    }
    ${CardText} {
      padding-left: var(--spacing-05);
    }
  }

  .ant-card-loading-content .ant-row:first-child {
    display: none;
  }
`;
const StyledTemplateCardIconWrapper = styled.div`
  height: 32px;
  width: 32px;
  background-color: var(--blue-10);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  color: var(--link-color);
  flex-shrink: 0;
`;

const StyledAiFillIconWrapper = styled(StyledTemplateCardIconWrapper)`
  background: linear-gradient(90.71deg, #ebf0ff 0.61%, #e8fff7 119.53%);
  width: 32px;
`;

export const StatusTemplatesListV2: FC<{
  visible: boolean;
  onClose(): void;
  onChooseTemplate(
    template: Template,
    type: StatusUpdatePrivacy,
    aiFillConfig?: AiFillRuntimeInput,
  ): void;
  createUpdate(privacy: StatusUpdatePrivacy): void;
  setAiFillRuntimeInput: React.Dispatch<
    React.SetStateAction<AiFillRuntimeInput | null>
  >;
  isPrivate: boolean;
}> = ({
  visible,
  onClose,
  onChooseTemplate,
  createUpdate,
  isPrivate,
  setAiFillRuntimeInput,
}) => {
  const { data: templates = [], isLoading: loading } =
    useGetAllStatusUpdateTemplatesQuery();

  const templatePrivacy = isPrivate
    ? StatusUpdatePrivacy.Private
    : StatusUpdatePrivacy.Shared;

  const createFromScratch = useTranslation('su.start.from.scratch');

  const newUpdate = useTranslation('templates.status_updates.new');

  const newPrivateUpdate = useTranslation(
    'templates.status_updates.new.private',
  );

  const privateTemplates = useMemo(
    () => templates.filter((template) => template.private),
    [templates],
  );

  const sharedTemplates = useMemo(
    () => templates.filter((template) => !template.private),
    [templates],
  );

  const templatesToShow = useMemo(
    () =>
      isPrivate ? [...privateTemplates, ...sharedTemplates] : sharedTemplates,
    [isPrivate, privateTemplates, sharedTemplates],
  );

  const [showAIDrawer, setShowAIDrawer] = useState(false);
  const [selectedTemplate, setTemplate] = useState<Template | null>(null);

  const onChooseTemplateWithAI = (aiFillConfig?: AiFillRuntimeInput) => {
    if (selectedTemplate) {
      onChooseTemplate(selectedTemplate, templatePrivacy);
      if (aiFillConfig !== undefined) {
        setAiFillRuntimeInput(aiFillConfig);
      }
    }
  };

  const meetingAiTemplates = templatesToShow.filter(
    (template) => !!template.templateMeta?.aiFillAppIds,
  );

  const generalTemplates = templatesToShow.filter(
    (template) => !template.templateMeta?.aiFillAppIds,
  );

  return (
    <CustomLayoutDrawer
      placement="right"
      closable
      closeIcon={
        <Tooltip
          placement="left"
          title={<FormattedMessage id="esc_to_close" />}
        >
          <Close16 />
        </Tooltip>
      }
      onClose={() => {
        onClose();
        setTimeout(() => setShowAIDrawer(false), 200);
      }}
      visible={visible}
      width={688}
    >
      <CustomLayoutDrawerContent>
        {showAIDrawer ? (
          <AIDrawerContent
            onChooseTemplate={(aiFillConfig?: AiFillRuntimeInput) => {
              onChooseTemplateWithAI(aiFillConfig);
              setTimeout(() => setShowAIDrawer(false), 200);
            }}
            onClose={() => {
              onClose();
              setTimeout(() => setShowAIDrawer(false), 200);
            }}
            onBack={() => setShowAIDrawer(false)}
          />
        ) : loading ? (
          <PageLoading />
        ) : templates.length > 0 ? (
          <>
            <CustomLayoutDrawerContent.Title>
              {isPrivate ? newPrivateUpdate : newUpdate}
            </CustomLayoutDrawerContent.Title>
            <CustomLayoutDrawerContent.Content>
              <Row>
                <CreateFromScratchCard
                  onClick={() => createUpdate(templatePrivacy)}
                >
                  <StyledCreateFromScratchIconWrapper data-cy="status_update_from_scratch">
                    <Add16 />
                  </StyledCreateFromScratchIconWrapper>
                  <StyledParagraph ellipsis={{ rows: 2 }}>
                    {isPrivate ? newPrivateUpdate : newUpdate}
                  </StyledParagraph>
                  <Help>{createFromScratch}</Help>
                </CreateFromScratchCard>
              </Row>
              <SectionWrapper>
                <SectionHeaderWrapper>
                  <SectionHeader>
                    <FormattedMessage id="status_updates.meeting_templates.heading" />
                  </SectionHeader>
                  <SectionSubText>
                    <FormattedMessage id="status_updates.meeting_templates.description" />
                  </SectionSubText>
                </SectionHeaderWrapper>
                <SectionContent>
                  <Row gutter={[16, 16]}>
                    {meetingAiTemplates.map((template) => (
                      <Col>
                        <StyledTemplateCard
                          bordered={false}
                          hoverable
                          onClick={() => {
                            setTemplate(template);
                            setShowAIDrawer(true);
                          }}
                          private={template.private}
                          data-cy={`status_update_template_${template.templateId}`}
                        >
                          <StyledAiFillIconWrapper>
                            <AIFill16 />
                          </StyledAiFillIconWrapper>
                          <CardText ellipsis={{ rows: 2 }}>
                            {template.templateName}
                          </CardText>
                        </StyledTemplateCard>
                      </Col>
                    ))}
                  </Row>
                </SectionContent>
              </SectionWrapper>
              <SectionWrapper>
                <SectionHeaderWrapper>
                  <SectionHeader>
                    <FormattedMessage id="status_updates.general_templates.heading" />
                  </SectionHeader>
                  <SectionSubText>
                    <FormattedMessage id="status_updates.general_templates.description" />
                  </SectionSubText>
                </SectionHeaderWrapper>
                <SectionContent>
                  <Row gutter={[16, 16]}>
                    {generalTemplates.map((template) => (
                      <Col>
                        <StyledTemplateCard
                          bordered={false}
                          hoverable
                          onClick={() => {
                            onChooseTemplate(template, templatePrivacy);
                          }}
                          private={template.private}
                          data-cy={`status_update_template_${template.templateId}`}
                        >
                          <StyledTemplateCardIconWrapper>
                            <Bullhorn20 />
                          </StyledTemplateCardIconWrapper>
                          <CardText ellipsis={{ rows: 2 }}>
                            {template.templateName}
                          </CardText>
                        </StyledTemplateCard>
                      </Col>
                    ))}
                  </Row>
                </SectionContent>
              </SectionWrapper>
            </CustomLayoutDrawerContent.Content>
          </>
        ) : (
          <CustomLayoutDrawerContent.Content>
            <StatusUpdatesTemplateEmptyState />
          </CustomLayoutDrawerContent.Content>
        )}
      </CustomLayoutDrawerContent>
    </CustomLayoutDrawer>
  );
};
